/**
 * Mobile sticky add-to-cart bar for the product page.
 * Renders a fixed footer with a collapsible variant picker, quantity selector,
 * price display, and add-to-cart button.
 *
 * On variant change the component:
 *   1. Instantly updates the form's hidden variant input and the CTA button.
 *   2. Mirrors the selection to the (hidden) desktop variant-picker so it fires
 *      its own section fetch — keeping the media gallery, URL, and desktop UI
 *      in sync without a second network request.
 *   3. Listens for the resulting `variant:update` event to swap the sticky bar's
 *      own price container with the freshly rendered one from the fetched HTML.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [backdrop] - The darkening backdrop behind the panel.
 * @property {HTMLButtonElement} [closeBtn] - The button that closes the panel.
 * @property {HTMLButtonElement} [cta] - The primary add-to-cart / open-panel button.
 * @property {HTMLFormElement} [form] - The product add-to-cart form.
 * @property {HTMLFormElement} [pickerForm] - The variant radio form inside the panel.
 *
 * @typedef {{ id: number, available: boolean, inventory_quantity: number, product_id: number, option1: string|null, option2: string|null, option3: string|null }} Variant
 */
class MobileStickyBar extends HTMLElement {
  connectedCallback() {
    this._backdrop = /** @type {HTMLElement | null} */ (this.querySelector('.mobile-sticky-bar__backdrop'));
    this._closeBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('.mobile-sticky-bar__close'));
    this._cta = /** @type {HTMLButtonElement | null} */ (this.querySelector('.mobile-sticky-bar__cta'));
    this._form = /** @type {HTMLFormElement | null} */ (this.querySelector('form[data-type="add-to-cart-form"]'));
    this._hasVariants = this.dataset.hasVariants === 'true';

    this._backdrop?.addEventListener('click', () => this.close());
    this._closeBtn?.addEventListener('click', () => this.close());

    this._cta?.addEventListener('click', () => {
      if (!this.classList.contains('is-open')) {
        this.open();
        return;
      }
      if (this._form) {
        this.close();
        this._form.requestSubmit
          ? this._form.requestSubmit()
          : this._form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });

    this._panel = /** @type {HTMLElement | null} */ (this.querySelector('.mobile-sticky-bar__panel'));
    this._footer = /** @type {HTMLElement | null} */ (this.querySelector('.mobile-sticky-bar__footer'));
    this._collapsible = /** @type {HTMLElement | null} */ (this.querySelector('.mobile-sticky-bar__collapsible'));
    requestAnimationFrame(() => this.#updateStickyHeightVar());

    if (this._panel && 'ResizeObserver' in window) {
      this._panelResizeObserver = new ResizeObserver(() => this.#updateStickyHeightVar());
      this._panelResizeObserver.observe(this._panel);
    }

    if (this._footer && 'ResizeObserver' in window) {
      this._footerResizeObserver = new ResizeObserver(() => this.#updateStickyHeightVar());
      this._footerResizeObserver.observe(this._footer);
    }

    window.addEventListener('resize', this.#updateStickyHeightVar);
    this._collapsible?.addEventListener('transitionend', this.#updateStickyHeightVar);

    const pickerForm = this.querySelector('.mobile-sticky-bar__picker-form');
    pickerForm?.addEventListener('change', () => this.#handleVariantChange());

    // Receive the VariantUpdateEvent that the desktop variant-picker dispatches
    // after its section fetch and use the returned HTML to refresh the price.
    this.closest('.shopify-section')?.addEventListener('variant:update', (e) =>
      this.#handleVariantUpdate(/** @type {CustomEvent} */ (e))
    );
  }

  disconnectedCallback() {
    this._panelResizeObserver?.disconnect();
    this._footerResizeObserver?.disconnect();
    window.removeEventListener('resize', this.#updateStickyHeightVar);
    this._collapsible?.removeEventListener('transitionend', this.#updateStickyHeightVar);
  }

  /** Syncs current sticky bar occupied height to a CSS variable used by floating widgets. */
  #updateStickyHeightVar = () => {
    if (!this._panel && !this._footer) return;
    const activeHeight = this._panel?.offsetHeight || this._footer?.offsetHeight || 0;
    document.documentElement.style.setProperty(
      '--mobile-sticky-bar-height',
      `${activeHeight}px`
    );
  };

  /**
   * Resolves the selected variant, instantly updates the form + CTA, then
   * delegates the section fetch to the desktop variant-picker by mirroring
   * the selection onto its radios and triggering one `change` event.
   */
  #handleVariantChange() {
    const variant = this.#resolveVariant();
    if (!variant) return;

    const variantInput = /** @type {HTMLInputElement | null} */ (
      this._form?.querySelector('input[name="id"]') ?? null
    );
    if (variantInput) variantInput.value = String(variant.id);

    if (this._cta) {
      this._cta.disabled = !variant.available;
      this._cta.textContent = variant.available
        ? (this._cta.dataset.atcText ?? '')
        : variant.inventory_quantity <= 0
          ? (this._cta.dataset.soldoutText ?? '')
          : (this._cta.dataset.unavailableText ?? '');
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    history.replaceState({}, '', url.toString());

    this.#syncToMainPicker();
  }

  /**
   * Mirrors all checked sticky-bar radios to the desktop `variant-picker` and
   * dispatches a single `change` event so `variant-picker.js` runs its full
   * update cycle (section fetch → VariantUpdateEvent → media gallery + URL).
   */
  #syncToMainPicker() {
    const mainPicker = this.closest('.shopify-section')?.querySelector(
      'variant-picker[data-template-product-match="true"]'
    );
    if (!mainPicker) return;

    const stickyChecked = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('.mobile-sticky-bar__picker-form input[type="radio"]:checked')
    );

    /** @type {HTMLInputElement | null} */
    let lastChanged = null;

    for (const radio of stickyChecked) {
      const mainRadio = /** @type {HTMLInputElement | null} */ (
        mainPicker.querySelector(`input[data-option-value-id="${radio.dataset.optionValueId}"]`)
      );
      if (mainRadio && !mainRadio.checked) {
        mainRadio.checked = true;
        lastChanged = mainRadio;
      }
    }

    // One change event causes variantChanged() to read all selectedOptionsValues
    // and build the correct multi-option fetch URL.
    lastChanged?.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Receives the `variant:update` event from the desktop variant-picker and
   * replaces the sticky bar's price container with the freshly rendered version
   * from the fetched section HTML (correct prices, badges, compare-at prices).
   * @param {CustomEvent} event
   */
  #handleVariantUpdate(event) {
    const html = /** @type {Document | undefined} */ (event.detail?.data?.html);
    if (!html) return;

    const newPrice = html.querySelector('.mobile-sticky-bar__footer .price-container-resmed');
    const currentPrice = this.querySelector('.mobile-sticky-bar__footer .price-container-resmed');
    if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);
  }

  /**
   * Finds the variant matching the currently checked radios by comparing
   * option value strings against the embedded variants JSON.
   * @returns {Variant | null}
   */
  #resolveVariant() {
    const pickerForm = this.querySelector('.mobile-sticky-bar__picker-form');
    if (!pickerForm) return null;

    /** @type {Variant[]} */
    let variants;
    try {
      variants = JSON.parse(
        pickerForm.querySelector('script[data-variants]')?.textContent || '[]'
      );
    } catch {
      return null;
    }

    const selected = /** @type {HTMLInputElement[]} */ (
      [...pickerForm.querySelectorAll('input[type="radio"]:checked')]
    )
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((r) => r.value);

    return (
      variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3].filter(Boolean);
        return opts.length === selected.length && selected.every((val, i) => opts[i] === val);
      }) ?? null
    );
  }

  /** Opens the collapsible variant panel. */
  open() {
    this.classList.add('is-open');
    document.body.classList.add('mobile-sticky-bar-open');
    requestAnimationFrame(this.#updateStickyHeightVar);
  }

  /** Closes the collapsible variant panel. */
  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('mobile-sticky-bar-open');
    requestAnimationFrame(this.#updateStickyHeightVar);
  }
}

if (!customElements.get('mobile-sticky-bar')) {
  customElements.define('mobile-sticky-bar', MobileStickyBar);
}
